import type {
  AbsenceChart,
  AssigneeOption,
  CommentItem,
  DepartmentNode,
  EmployeeDetails,
  EmployeeListItem,
  InboxCounters,
  InboxItem,
  NotificationItem,
  Paginated,
  ProjectItem,
  RequestDetails,
  RequestListItem,
  RouteSettingsView,
  TaskCounters,
  TaskDetails,
  TaskListItem,
  TaskSort,
  TaskView,
  VacationDetails,
  VacationListItem,
} from '@erp/shared';
import { queryOptions } from '@tanstack/react-query';
import { api } from './api';

export const departmentsTreeQuery = queryOptions({
  queryKey: ['org', 'tree'],
  queryFn: () => api<DepartmentNode[]>('/org/departments/tree'),
  staleTime: 30_000,
});

export const positionsQuery = queryOptions({
  queryKey: ['org', 'positions'],
  queryFn: () => api<{ id: string; name: string }[]>('/org/positions'),
  staleTime: 5 * 60_000,
});

export interface EmployeesFilter {
  q?: string;
  departmentId?: string;
  includeSubdepartments?: boolean;
  page?: number;
}

export const employeesQuery = (f: EmployeesFilter) =>
  queryOptions({
    queryKey: ['org', 'employees', f],
    queryFn: () => api<Paginated<EmployeeListItem>>('/org/employees', { query: { ...f, pageSize: 50 } }),
    placeholderData: (prev) => prev,
  });

export const employeeQuery = (id: string) =>
  queryOptions({
    queryKey: ['org', 'employee', id],
    queryFn: () => api<EmployeeDetails>(`/org/employees/${id}`),
  });

export const commentsQuery = (entityType: string, entityId: string) =>
  queryOptions({
    queryKey: ['comments', entityType, entityId],
    queryFn: () => api<CommentItem[]>('/comments', { query: { entityType, entityId } }),
  });

export const notificationsQuery = queryOptions({
  queryKey: ['notifications', 'list'],
  queryFn: () => api<NotificationItem[]>('/notifications', { query: { limit: 50 } }),
});

/** Плоский список отделов с отступами — для выпадающих списков. */
export function flattenTree(nodes: DepartmentNode[], depth = 0): { node: DepartmentNode; depth: number }[] {
  return nodes.flatMap((n) => [{ node: n, depth }, ...flattenTree(n.children, depth + 1)]);
}

// ---------- Задачи ----------

export interface TasksFilter {
  view: TaskView;
  projectId?: string;
  q?: string;
  overdue?: boolean;
  dueSoon?: boolean;
  unread?: boolean;
  noDeadline?: boolean;
  status?: string;
  order?: 'deadline' | 'board' | 'recent';
  includeClosed?: boolean;
  creatorId?: string;
  assigneeId?: string;
  deadlineFrom?: string;
  deadlineTo?: string;
  createdFrom?: string;
  createdTo?: string;
  closedFrom?: string;
  closedTo?: string;
  sort?: TaskSort;
  dir?: 'asc' | 'desc';
}

export const tasksQuery = (f: TasksFilter) =>
  queryOptions({
    queryKey: ['tasks', 'list', f],
    queryFn: () => api<Paginated<TaskListItem>>('/tasks', { query: { ...f, pageSize: 500 } }),
    placeholderData: (prev) => prev,
  });

export const taskQuery = (id: string) =>
  queryOptions({
    queryKey: ['tasks', 'item', id],
    queryFn: () => api<TaskDetails>(`/tasks/${id}`),
  });

export const taskCountersQuery = queryOptions({
  queryKey: ['tasks', 'counters'],
  queryFn: () => api<TaskCounters>('/tasks/counters'),
  refetchInterval: 120_000,
});

export const assigneesQuery = queryOptions({
  queryKey: ['tasks', 'assignees'],
  queryFn: () => api<AssigneeOption[]>('/tasks/assignees'),
  staleTime: 5 * 60_000,
});

export const projectsQuery = queryOptions({
  queryKey: ['tasks', 'projects'],
  queryFn: () => api<ProjectItem[]>('/projects'),
  staleTime: 60_000,
});

// ---------- Согласования, отпуска, заявки ----------

export const inboxQuery = (state: 'open' | 'done') =>
  queryOptions({
    queryKey: ['bpm', 'inbox', state],
    queryFn: () => api<InboxItem[]>('/bpm/inbox', { query: { state } }),
  });

export const inboxCountersQuery = queryOptions({
  queryKey: ['bpm', 'counters'],
  queryFn: () => api<InboxCounters>('/bpm/inbox/counters'),
  refetchInterval: 120_000,
});

export const routeSettingsQuery = queryOptions({
  queryKey: ['bpm', 'routes'],
  queryFn: () => api<RouteSettingsView[]>('/bpm/routes'),
});

export interface VacationsFilter {
  scope: 'mine' | 'all';
  status?: string;
  type?: string;
  userId?: string;
  from?: string;
  to?: string;
}

export const vacationsQuery = (f: VacationsFilter) =>
  queryOptions({
    queryKey: ['vacations', 'list', f],
    queryFn: () => api<Paginated<VacationListItem>>('/vacations', { query: { ...f, pageSize: 200 } }),
    placeholderData: (prev) => prev,
  });

export const vacationQuery = (id: string) =>
  queryOptions({
    queryKey: ['vacations', 'item', id],
    queryFn: () => api<VacationDetails>(`/vacations/${id}`),
  });

export const absenceChartQuery = (f: { from: string; to: string; departmentId?: string }) =>
  queryOptions({
    queryKey: ['absences', f],
    queryFn: () => api<AbsenceChart>('/absences', { query: f }),
    placeholderData: (prev) => prev,
  });

export interface RequestsFilter {
  scope: 'mine' | 'all';
  type?: string;
  state?: 'open' | 'closed';
  q?: string;
  userId?: string;
  createdFrom?: string;
  createdTo?: string;
}

export const requestsQuery = (f: RequestsFilter) =>
  queryOptions({
    queryKey: ['requests', 'list', f],
    queryFn: () => api<Paginated<RequestListItem>>('/requests', { query: { ...f, pageSize: 200 } }),
    placeholderData: (prev) => prev,
  });

export const requestQuery = (id: string) =>
  queryOptions({
    queryKey: ['requests', 'item', id],
    queryFn: () => api<RequestDetails>(`/requests/${id}`),
  });
